import axios from "axios";
import {IOrganizationSegmentsResponse} from "../hooks/useFetchDataCrgciAuthorities";
import {
  IPeople,
  IGetResponceFromCms,
  IWorkerWithInitiatives,
  IWorkerWithConnections,
  IWorkerWithPhoto,
  IWorkerWithComments,
  IInitiativeApiSingleResponse,
} from "../pages/StateInitiative/StateInitiative.type";

export const fetchRadarData = async (): Promise<any> => {
  try {
    const response = await axios.get(
      `https://gpb-radar.sandcloud.ru/api/radar`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching radar data:", error);
    return null;
  }
};

export const fetchDirectionData = async (id: number): Promise<any> => {
  try {
    const response = await axios.get(
      `https://gpb-radar.sandcloud.ru/api/direction/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching direction data:", error);
    return null;
  }
};

export const fetchClusterData = async (id: number): Promise<any> => {
  try {
    const response = await axios.get(
      `https://gpb-radar.sandcloud.ru/api/cluster/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching cluster data:", error);
    return null;
  }
};

export const fetchTechnologyData = async (id: number): Promise<any> => {
  try {
    const response = await axios.get(
      `https://gpb-radar.sandcloud.ru/api/technology/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching technology data:", error);
    return null;
  }
};

export const fetchWorkerData = async (
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string
): Promise<IGetResponceFromCms<IPeople> | null> => {
  try {
    if (!apiUrl || !apiKey || !documentId) {
      console.error("API URL, API key, or documentId is missing");
      return null;
    }

    const headers = {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    };

    // Сначала делаем запрос с populate=photo
    const url = new URL(`${apiUrl}/api/people`);
    url.searchParams.set("filters[documentId][$eq]", documentId);
    url.searchParams.set("populate", "photo");

    const response = await axios.get(url.toString(), {headers});

    // API с фильтрами возвращает массив, берем первый элемент
    if (response.data?.data && response.data.data.length > 0) {
      return {
        data: response.data.data[0],
        meta: response.data.meta,
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching worker data:", error);
    console.error("Request details:", {
      url: `${apiUrl}/api/people?filters[documentId][$eq]=${documentId}`,
      documentId,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

export const fetchWorkerComments = async (
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string
): Promise<any[]> => {
  try {
    if (!apiUrl || !apiKey || !documentId) {
      return [];
    }

    const headers = {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    };

    const url = new URL(`${apiUrl}/api/people`);
    url.searchParams.set("filters[documentId][$eq]", documentId);
    url.searchParams.set("populate", "comments");

    const response = await axios.get(url.toString(), {headers});

    if (response.data?.data && response.data.data.length > 0) {
      return response.data.data[0].comments || [];
    }

    return [];
  } catch (error) {
    console.error("Error fetching worker comments:", error);
    return [];
  }
};

// Перегрузки функции для типизации на основе populate параметра
export async function fetchWorkerRelatedData(
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string,
  populate: "initiatives.initiative"
): Promise<IWorkerWithInitiatives | null>;

export async function fetchWorkerRelatedData(
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string,
  populate: "connections.photo" | "connections.connections"
): Promise<IWorkerWithConnections | null>;

export async function fetchWorkerRelatedData(
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string,
  populate: "photo"
): Promise<IWorkerWithPhoto | null>;

export async function fetchWorkerRelatedData(
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string,
  populate: "comments"
): Promise<IWorkerWithComments | null>;

export async function fetchWorkerRelatedData(
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string,
  populate: string
): Promise<any>;

// Реализация функции для загрузки данных с populate отдельно
export async function fetchWorkerRelatedData(
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string,
  populate: string
): Promise<any> {
  try {
    if (!apiUrl || !apiKey || !documentId) {
      return null;
    }

    const headers = {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    };

    const url = new URL(`${apiUrl}/api/people`);
    url.searchParams.set("filters[documentId][$eq]", documentId);
    url.searchParams.set("populate", populate);

    const response = await axios.get(url.toString(), {headers});

    if (response.data?.data && response.data.data.length > 0) {
      return response.data.data[0];
    }

    return null;
  } catch (error) {
    console.error(`Error fetching worker ${populate} data:`, error);
    if (axios.isAxiosError(error)) {
      console.error(
        `HTTP ${error.response?.status}: ${error.response?.statusText}`
      );
      console.error("Response data:", error.response?.data);
    }
    return null;
  }
}

export const fetchSegmentsData = async (
  apiUrl: string | undefined,
  apiKey: string | undefined
): Promise<IOrganizationSegmentsResponse | null> => {
  try {
    if (!apiUrl || !apiKey) {
      console.error("API URL or API key is missing");
      return null;
    }

    const headers = {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    };

    const url = new URL(`${apiUrl}/api/organization-segments`);

    url.searchParams.set("populate[0]", "organizations");
    url.searchParams.set("populate[1]", "organizations.initiatives");
    url.searchParams.set("populate[2]", "organizations.employees.photo");

    const response = await axios.get(url.toString(), {headers});
    return response.data;
  } catch (error) {
    console.error("Error fetching segments data:", error);
    return null;
  }
};

export const fetchInitiativeByIdData = async (
  apiUrl: string | undefined,
  apiKey: string | undefined,
  documentId: string
): Promise<IInitiativeApiSingleResponse | null> => {
  try {
    if (!apiUrl || !apiKey) {
      console.error("API URL or API key is missing");
      return null;
    }

    const headers = {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    };

    const url = `${apiUrl}/api/initiatives?filters[documentId][$eq]=${documentId}&populate[participants][populate][person][populate][organization][populate][segment][populate]=*&populate[impact_areas][fields]=name&populate[comments][populate]=*&populate[initiator_org][fields][0]=id&populate[initiator_org][fields][1]=name&populate[legal_basis][populate]=*`;

    const response = await axios.get(url, {headers});
    return response.data;
  } catch (error) {
    console.error("Error fetching initiative by id data:", error);
    return null;
  }
};
